/**
 * KontoauszuegePage — Wöchentlicher Bank-CSV-Upload + Abgleich (Feature bank-csv-abgleich)
 * Erweitert um Buchhaltungs-Cockpit (Feature bank-buchen-cockpit):
 *   - Kontokopf (Bankkonto der Firma aus finanz_konten)
 *   - Status-Leiste aus v_buchhaltung_status
 *   - aufklappbare Zeilen mit Buchungskonto-Vorschlag und „Buchen“ (additiv)
 *
 * Eingebettet im Finanzen-Cockpit (Sektion „Bank“, Route
 * /finanzen/:firma/kontoauszuege). Erhält die Firma als Prop `firmaKey`
 * ('sv' | 'lcs'); ohne Prop → 'sv' (SV Büro).
 *
 * GoBD: ausschließlich Vorschläge + manuelle Bestätigung. Keine Auto-Buchung.
 * Keine Buchung ohne belegmäßige Zuordnung (Rechnung/Akte bzw. Beleg).
 * RLS unverändert (Inhaber-only).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, X, Loader2,
  ArrowUpRight, ArrowDownRight, Link2, Receipt, Clock, RefreshCw,
  Landmark, ChevronDown, ChevronRight, BookCheck,
  FileText, Image as ImageIcon, ExternalLink,
} from 'lucide-react';
import {
  useBankAbgleich, erkenneFirmaAusZeilen, FIRMA_ABGLEICH,
  type FirmaKey, type ImportTransaktionRow, type FinanzKonto,
  type RechnungsMatch,
} from '@/hooks/useBankAbgleich';
import {
  parseSparkassenCsv, berechneVorschau, cleanVerwendungszweck,
  type BankCsvZeile, type BankCsvVorschau,
} from '@/lib/bankCsv';
import { supabase } from '@/lib/supabase';
import { schlageKontoVor, lerneLernregel } from '@/lib/belegKonto';

const STATUS_LABEL: Record<string, string> = {
  unklar: 'Offen',
  sicher: 'Automatisch',
  geklaert: 'Geklärt',
  ignoriert: 'Ignoriert',
};

// Firma-Mapping für eingangsbelege.firma (siehe BuchhaltungEinOrtPage / FirmaId):
//   sv  → 'gutachter_koop',  lcs → 'lcs_gmbh'
const BELEG_FIRMA: Record<FirmaKey, string> = {
  sv: 'gutachter_koop',
  lcs: 'lcs_gmbh',
  privat: 'privat',
  immobilien: 'privat',
};

// finanz_konten.firma weicht für LCS vom import_transaktionen.firma-Wert ab.
//   sv  → 'sv_buero_koop',  lcs → 'lcs_gmbh'
const FINANZ_KONTO_FIRMA: Record<FirmaKey, string> = {
  sv: 'sv_buero_koop',
  lcs: 'lcs_gmbh',
  privat: 'privat',
  immobilien: 'privat',
};

// Gegenkonto (Geldkonto SKR03) je Firma für die Buchung.
const GEGENKONTO: Record<FirmaKey, string> = {
  sv: '1200',
  lcs: '1800',
  privat: '1200',
  immobilien: '1200',
};

function fmtBetrag(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDatum(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

/** Offener Eingangsbeleg, der zu einem Ausgang passen könnte. */
interface OffenerBeleg {
  id: string;
  firma: string;
  absender_firma: string | null;
  absender_name: string | null;
  verwendungszweck: string | null;
  brutto_betrag: number | null;
  belegdatum: string | null;
}

/** Bankkonto der Firma (Kontokopf). */
interface BankKontoKopf {
  id: string;
  name: string;
  iban: string | null;
  bank_name: string | null;
  aktueller_saldo: number | null;
  typ: string | null;
}

/** Status-Kennzahlen aus v_buchhaltung_status. */
interface BuchhaltungStatus {
  firma: string;
  tx_gesamt: number | null;
  tx_offen: number | null;
  tx_geklaert: number | null;
  belege_gesamt: number | null;
  belege_ohne_zahlung: number | null;
  belege_nicht_gebucht: number | null;
  buchungen_gesamt: number | null;
  buchungen_gebucht: number | null;
  buchungen_vorerfasst: number | null;
  buchungen_an_stb: number | null;
}

/** Buchungskonto (SKR-Konto) der Firma aus der Tabelle buchungskonten. */
interface Buchungskonto {
  id: string;
  firma: string;
  skr: string | null;
  nummer: string | null;
  bezeichnung: string | null;
  typ: 'erloes' | 'aufwand' | 'aktiv' | 'passiv' | 'steuer' | 'sonstiges' | string | null;
  bwa_gruppe: string | null;
  steuersatz: number | null;
  ist_aktiv: boolean | null;
  sortierung: number | null;
}

/**
 * Lädt die aktiven Buchungskonten der Firma (firma = cfg.importFirma),
 * sortiert nach `sortierung`. Robust — liefert im Fehlerfall [].
 */
async function ladeBuchungskonten(importFirma: string): Promise<Buchungskonto[]> {
  try {
    const { data, error } = await supabase
      .from('buchungskonten')
      .select('id, firma, skr, nummer, bezeichnung, typ, bwa_gruppe, steuersatz, ist_aktiv, sortierung')
      .eq('firma', importFirma)
      .eq('ist_aktiv', true)
      .order('sortierung', { ascending: true });
    if (error) throw error;
    return (data as Buchungskonto[]) || [];
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Buchungskonten laden fehlgeschlagen:', err);
    return [];
  }
}

/**
 * Bestimmt den Default-Buchungskonto-Vorschlag für eine Transaktion.
 *   - Einnahme (betrag > 0): erstes Konto typ = 'erloes'.
 *   - Ausgabe (betrag < 0): bestes buchungs_lernregeln-Match
 *     (firma + lieferant_muster im rohtext, mit buchungskonto_id) — sonst
 *     erstes Konto typ = 'aufwand'.
 * Liefert die Buchungskonto-id (oder '' falls keine passt). Wirft NICHT.
 */
async function bestimmeVorschlagKontoId(
  importFirma: string,
  tx: ImportTransaktionRow,
  konten: Buchungskonto[],
): Promise<string> {
  try {
    const istEinnahme = tx.betrag > 0;
    if (istEinnahme) {
      const erloes = konten.find((k) => k.typ === 'erloes');
      return erloes?.id || konten[0]?.id || '';
    }

    // Ausgabe: Lernregel mit buchungskonto_id bevorzugen.
    const text = tx.rohtext || tx.beschreibung || '';
    try {
      const lern = await ladeLernregelKonto(importFirma, text, konten);
      if (lern) return lern;
    } catch {
      // Lernregel-Fehler ignorieren — Heuristik/Fallback greift.
    }

    // Zusatzheuristik (optional): schlageKontoVor kann eine Kategorie liefern;
    // wird hier nur defensiv aufgerufen, beeinflusst aber nicht das Konto.
    try {
      await schlageKontoVor(importFirma, text);
    } catch {
      // optional — Ergebnis hier nicht zwingend genutzt.
    }

    const aufwand = konten.find((k) => k.typ === 'aufwand');
    return aufwand?.id || konten[0]?.id || '';
  } catch {
    return konten[0]?.id || '';
  }
}

/**
 * Sucht in buchungs_lernregeln die beste Regel (firma + lieferant_muster im
 * Text) mit gesetzter buchungskonto_id und gibt die id zurück, sofern sie zu
 * einem geladenen Buchungskonto gehört. Robust — liefert null bei Fehler.
 */
async function ladeLernregelKonto(
  firma: string,
  text: string,
  konten: Buchungskonto[],
): Promise<string | null> {
  const muster = (text || '').toLowerCase().trim();
  if (!muster) return null;
  try {
    const { data, error } = await supabase
      .from('buchungs_lernregeln')
      .select('lieferant_muster, buchungskonto_id, treffer')
      .eq('firma', firma)
      .not('buchungskonto_id', 'is', null)
      .order('treffer', { ascending: false })
      .limit(200);
    if (error) throw error;
    const regeln = (data as {
      lieferant_muster: string | null;
      buchungskonto_id: string | null;
      treffer: number | null;
    }[]) || [];

    const gueltigeIds = new Set(konten.map((k) => k.id));
    let beste: { id: string; treffer: number } | null = null;
    for (const r of regeln) {
      const rm = (r.lieferant_muster || '').toLowerCase().trim();
      const id = r.buchungskonto_id;
      if (!rm || !id || !gueltigeIds.has(id)) continue;
      const passt = muster === rm || muster.includes(rm) || rm.includes(muster);
      if (!passt) continue;
      const tr = r.treffer || 0;
      if (!beste || tr > beste.treffer) beste = { id, treffer: tr };
    }
    return beste?.id || null;
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Lernregel-Konto-Suche fehlgeschlagen:', err);
    return null;
  }
}

/** Gebuchte Transaktion: kompakte Info zur Anzeige des „Gebucht“-Badges. */
interface VorhandeneBuchung {
  id: string;
  buchungskonto_id: string | null;
  nummer: string | null;
  bezeichnung: string | null;
  status: string | null;
}

/**
 * Prüft, ob zu einer Transaktion bereits eine Buchung existiert
 * (buchungen.import_transaktion_id = tx.id) und liefert die Konto-Anzeige.
 * Robust — liefert null bei Fehler/keinem Treffer.
 */
async function ladeVorhandeneBuchung(
  txId: string,
  konten: Buchungskonto[],
): Promise<VorhandeneBuchung | null> {
  try {
    const { data, error } = await supabase
      .from('buchungen')
      .select('id, buchungskonto_id, status')
      .eq('import_transaktion_id', txId)
      .limit(1);
    if (error) throw error;
    const row = (data as { id: string; buchungskonto_id: string | null; status: string | null }[] | null)?.[0];
    if (!row) return null;
    const konto = konten.find((k) => k.id === row.buchungskonto_id);
    return {
      id: row.id,
      buchungskonto_id: row.buchungskonto_id,
      nummer: konto?.nummer ?? null,
      bezeichnung: konto?.bezeichnung ?? null,
      status: row.status,
    };
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Vorhandene Buchung laden fehlgeschlagen:', err);
    return null;
  }
}

/**
 * Prüft, ob zu einer Transaktion ein Eingangsbeleg verknüpft ist
 * (eingangsbelege.import_transaktion_id = tx.id). Robust — false bei Fehler.
 */
async function hatVerknuepftenBeleg(txId: string): Promise<{ vorhanden: boolean; belegId: string | null }> {
  try {
    const { data, error } = await supabase
      .from('eingangsbelege')
      .select('id')
      .eq('import_transaktion_id', txId)
      .limit(1);
    if (error) throw error;
    const row = (data as { id: string }[] | null)?.[0];
    return { vorhanden: !!row, belegId: row?.id || null };
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Beleg-Verknüpfung-Pruefung fehlgeschlagen:', err);
    return { vorhanden: false, belegId: null };
  }
}

/**
 * Vorschau eines sicher verknüpften Eingangsbelegs (für die Miniatur im
 * Detailbereich). `istBild`/`istPdf` steuern die Darstellung.
 */
interface BelegVorschau {
  belegId: string;
  storagePath: string;
  signedUrl: string;
  dateiName: string;
  istBild: boolean;
  istPdf: boolean;
}

/** Endung aus einem Storage-Pfad/Dateinamen (lowercase, ohne Punkt). */
function dateiEndung(pfad: string): string {
  const clean = (pfad || '').split('?')[0].split('#')[0];
  const teil = clean.split('/').pop() || clean;
  const punkt = teil.lastIndexOf('.');
  return punkt >= 0 ? teil.slice(punkt + 1).toLowerCase() : '';
}

/**
 * Lädt — NUR wenn ein Eingangsbeleg sicher mit DIESER Transaktion verknüpft ist
 * (eingangsbelege.import_transaktion_id = txId) — eine Belegvorschau:
 *   eingangsbelege.inbox_item_id → inbox_items.storage_path →
 *   Signed URL via supabase.storage.from('inbox').createSignedUrl(path, 600).
 * Sehr robust (try/catch, kein Crash). Liefert null, wenn kein verknüpfter
 * Beleg / kein Pfad / keine Signed URL ermittelbar ist.
 */
async function ladeVerknuepfteBelegVorschau(txId: string): Promise<BelegVorschau | null> {
  try {
    const { data: belegData, error: belegErr } = await supabase
      .from('eingangsbelege')
      .select('id, inbox_item_id')
      .eq('import_transaktion_id', txId)
      .limit(1);
    if (belegErr) throw belegErr;
    const beleg = (belegData as { id: string; inbox_item_id: string | null }[] | null)?.[0];
    if (!beleg || !beleg.inbox_item_id) return null;

    const { data: itemData, error: itemErr } = await supabase
      .from('inbox_items')
      .select('storage_path')
      .eq('id', beleg.inbox_item_id)
      .limit(1);
    if (itemErr) throw itemErr;
    const item = (itemData as { storage_path: string | null }[] | null)?.[0];
    const storagePath = item?.storage_path || '';
    if (!storagePath) return null;

    const { data: urlData, error: urlErr } = await supabase
      .storage
      .from('inbox')
      .createSignedUrl(storagePath, 600);
    if (urlErr) throw urlErr;
    const signedUrl = urlData?.signedUrl || '';
    if (!signedUrl) return null;

    const endung = dateiEndung(storagePath);
    const istBild = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(endung);
    const istPdf = endung === 'pdf';
    const dateiName = storagePath.split('/').pop() || storagePath;

    return { belegId: beleg.id, storagePath, signedUrl, dateiName, istBild, istPdf };
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Beleg-Vorschau laden fehlgeschlagen:', err);
    return null;
  }
}

/** Rechnung einer Akte (read-only) für den Forderungs-Restbetrag (Schritt 3). */
interface RechnungOffen {
  id: string;
  rechnungs_nr: string | null;
  brutto_betrag: number | null;
  bezahlt_betrag: number | null;
  kuerzung_betrag: number | null;
  status: string | null;
  offen: number;
}

/** Ergebnis der Forderungs-Suche zu einer Akte (read-only). */
interface ForderungsInfo {
  akteId: string;
  aktenNr: string;
  rechnungen: RechnungOffen[];
}

/**
 * Extrahiert aus einem Verwendungszweck ein Aktenzeichen nach Muster
 * `AK` + 5–9 Ziffern und bildet aktenNr = 'AK' + Ziffern. Liefert null, wenn
 * kein Muster gefunden wird.
 */
function extrahiereAktenNr(text: string): string | null {
  if (!text) return null;
  const m = text.match(/AK[\s\-_.]?(\d{5,9})/i);
  if (!m) return null;
  return 'AK' + m[1];
}

/**
 * Lädt (NUR LESEND) für einen Geldeingang mit Aktenzeichen im Verwendungszweck
 * die offenen Rechnungsbeträge:
 *   akten (akten_nr = aktenNr) → akten.id; rechnungen (akte_id = akten.id).
 * Offen = brutto_betrag − coalesce(bezahlt_betrag,0) − coalesce(kuerzung_betrag,0).
 * KEINE Schreibzugriffe. Sehr robust (try/catch) — liefert null bei Fehler/Nichtfund.
 */
async function ladeForderungsInfo(verwendungszweck: string): Promise<ForderungsInfo | null> {
  try {
    const aktenNr = extrahiereAktenNr(verwendungszweck || '');
    if (!aktenNr) return null;

    const { data: aktenData, error: aktenErr } = await supabase
      .from('akten')
      .select('id, akten_nr')
      .eq('akten_nr', aktenNr)
      .limit(1);
    if (aktenErr) throw aktenErr;
    const akte = (aktenData as { id: string; akten_nr: string | null }[] | null)?.[0];
    if (!akte || !akte.id) return null;

    const { data: rData, error: rErr } = await supabase
      .from('rechnungen')
      .select('id, rechnungs_nr, brutto_betrag, bezahlt_betrag, kuerzung_betrag, status')
      .eq('akte_id', akte.id);
    if (rErr) throw rErr;

    const rows = (rData as {
      id: string;
      rechnungs_nr: string | null;
      brutto_betrag: number | null;
      bezahlt_betrag: number | null;
      kuerzung_betrag: number | null;
      status: string | null;
    }[] | null) || [];

    const rechnungen: RechnungOffen[] = rows.map((r) => {
      const brutto = Number(r.brutto_betrag) || 0;
      const bezahlt = Number(r.bezahlt_betrag) || 0;
      const kuerzung = Number(r.kuerzung_betrag) || 0;
      const offen = brutto - bezahlt - kuerzung;
      return {
        id: r.id,
        rechnungs_nr: r.rechnungs_nr,
        brutto_betrag: r.brutto_betrag,
        bezahlt_betrag: r.bezahlt_betrag,
        kuerzung_betrag: r.kuerzung_betrag,
        status: r.status,
        offen,
      };
    });

    if (rechnungen.length === 0) return null;
    return { akteId: akte.id, aktenNr, rechnungen };
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Forderungs-Info laden fehlgeschlagen:', err);
    return null;
  }
}

/**
 * Führt die eigentliche Buchung aus: INSERT in `buchungen`, UPDATE der
 * import_transaktion auf 'geklaert' und (robust) Upsert einer Lernregel.
 * Lernregel-Fehler dürfen die Buchung NICHT abbrechen. Wirft NICHT — liefert
 * { ok, fehler } zurück.
 */
async function fuehreBuchungAus(params: {
  importFirma: string;
  tx: ImportTransaktionRow;
  buchungskontoId: string;
  buchungstext: string;
  gegenkonto: string;
  belegVorhanden: boolean;
  belegId: string | null;
  status: 'gebucht' | 'vorerfasst';
}): Promise<{ ok: boolean; fehler?: string }> {
  const { importFirma, tx, buchungskontoId, buchungstext, gegenkonto, belegVorhanden, belegId, status } = params;
  try {
    const { error: bErr } = await supabase
      .from('buchungen')
      .insert({
        firma: importFirma,
        buchung_datum: tx.buchung_datum,
        betrag: tx.betrag,
        buchungskonto_id: buchungskontoId,
        gegenkonto,
        buchungstext: buchungstext || null,
        import_transaktion_id: tx.id,
        eingangsbeleg_id: belegId,
        beleg_vorhanden: belegVorhanden,
        status,
        quelle: 'bank_cockpit',
      } as any);
    if (bErr) throw bErr;

    // import_transaktion als geklärt markieren.
    const { error: tErr } = await supabase
      .from('import_transaktionen')
      .update({ status: 'geklaert' } as any)
      .eq('id', tx.id);
    if (tErr) {
      // Buchung ist gespeichert; Status-Update-Fehler nur protokollieren.
      console.error('import_transaktion Status-Update fehlgeschlagen:', tErr);
    }

    // Lernregel anlernen (robust — Fehler hier nie fatal).
    try {
      const muster = ersterLieferant(tx.rohtext || tx.beschreibung || '');
      if (muster) {
        await upsertLernregelKonto(importFirma, muster, buchungskontoId);
      }
    } catch (lernErr) {
      console.error('Lernregel-Anlernen fehlgeschlagen (nicht fatal):', lernErr);
    }

    return { ok: true };
  } catch (err: any) {
    console.error('Buchung fehlgeschlagen:', err);
    return { ok: false, fehler: err?.message || 'Unbekannter Fehler' };
  }
}

/** Erstes aussagekräftiges Wort/Lieferant aus dem Rohtext (für die Lernregel). */
function ersterLieferant(text: string): string {
  const clean = cleanVerwendungszweck(text || '');
  const basis = (clean || text || '').toLowerCase().replace(/[^a-z0-9äöüß\s]+/gi, ' ').replace(/\s+/g, ' ').trim();
  if (!basis) return '';
  // Erste 1–3 Wörter als Muster (kompakt, aber unterscheidbar).
  return basis.split(' ').slice(0, 3).join(' ');
}

/**
 * Upsert einer Lernregel mit buchungskonto_id (firma + lieferant_muster).
 * Nutzt bevorzugt lerneLernregel; fällt robust auf einen direkten Upsert
 * zurück, da lerneLernregel die buchungskonto_id-Spalte ggf. nicht setzt.
 */
async function upsertLernregelKonto(
  firma: string,
  muster: string,
  buchungskontoId: string,
): Promise<void> {
  // 1) Bestehenden Lernmechanismus mitlaufen lassen (zählt Treffer hoch).
  try {
    await lerneLernregel({ firma, text: muster, kategorie_id: null, konto_skr: null });
  } catch {
    // optional — nicht fatal.
  }
  // 2) buchungskonto_id direkt setzen (Spalte neu in buchungs_lernregeln).
  try {
    const { error } = await supabase
      .from('buchungs_lernregeln')
      .upsert(
        { firma, lieferant_muster: muster, buchungskonto_id: buchungskontoId } as any,
        { onConflict: 'firma,lieferant_muster' },
      );
    if (error) throw error;
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Lernregel buchungskonto_id-Upsert fehlgeschlagen:', err);
  }
}

function isTableNotFound(error: any): boolean {
  if (!error) return false;
  const msg = error.message || error.code || '';
  return (
    msg.includes('PGRST205') ||
    msg.includes('schema cache') ||
    msg.includes('relation') ||
    msg.includes('does not exist')
  );
}

/** ISO-Datum (YYYY-MM-DD) um delta Tage verschieben. */
function verschiebeTage(iso: string, delta: number): string {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
}

/**
 * Lädt das Geschäftskonto (Bankkonto) der Firma für den Kontokopf.
 * Bevorzugt typ ~ 'bank' bzw. Name enthält 'Geschäftskonto'. Robust gegen
 * fehlende Tabelle/Spalten — liefert im Fehlerfall null.
 */
async function ladeBankKonto(finanzFirma: string): Promise<BankKontoKopf | null> {
  try {
    const { data, error } = await supabase
      .from('finanz_konten')
      .select('id, name, iban, bank_name, aktueller_saldo, typ, ist_aktiv')
      .eq('firma', finanzFirma)
      .limit(50);
    if (error) throw error;
    const konten = (data as (BankKontoKopf & { ist_aktiv?: boolean | null })[]) || [];
    if (konten.length === 0) return null;
    // Aktive Konten bevorzugen.
    const aktive = konten.filter((k) => k.ist_aktiv !== false);
    const pool = aktive.length > 0 ? aktive : konten;
    // 1) Name enthält 'Geschäftskonto'  2) typ ~ 'bank'  3) erstes Konto.
    const treffer =
      pool.find((k) => (k.name || '').toLowerCase().includes('geschäftskonto')) ||
      pool.find((k) => (k.name || '').toLowerCase().includes('geschaeftskonto')) ||
      pool.find((k) => (k.typ || '').toLowerCase().includes('bank')) ||
      pool[0];
    return treffer || null;
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Bankkonto laden fehlgeschlagen:', err);
    return null;
  }
}

/**
 * Lädt die Status-Kennzahlen der Firma aus v_buchhaltung_status.
 * Robust — liefert im Fehlerfall null.
 */
async function ladeBuchhaltungStatus(importFirma: string): Promise<BuchhaltungStatus | null> {
  try {
    const { data, error } = await supabase
      .from('v_buchhaltung_status')
      .select('*')
      .eq('firma', importFirma)
      .limit(1);
    if (error) throw error;
    const row = (data as BuchhaltungStatus[] | null)?.[0];
    return row || null;
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Buchhaltungs-Status laden fehlgeschlagen:', err);
    return null;
  }
}

/**
 * Lädt passende offene Eingangsbelege zu einem Ausgang:
 *   brutto_betrag ≈ |betrag| (±0,02), belegdatum ±10 Tage, import_transaktion_id IS NULL.
 * Robust gegen fehlende Tabelle/Spalten — liefert im Fehlerfall [].
 */
async function ladePassendeBelege(
  belegFirma: string,
  tx: ImportTransaktionRow,
): Promise<OffenerBeleg[]> {
  try {
    const ziel = Math.abs(tx.betrag);
    const min = ziel - 0.02;
    const max = ziel + 0.02;

    let query = supabase
      .from('eingangsbelege')
      .select('id, firma, absender_firma, absender_name, verwendungszweck, brutto_betrag, belegdatum')
      .eq('firma', belegFirma)
      .is('import_transaktion_id', null)
      .gte('brutto_betrag', min)
      .lte('brutto_betrag', max)
      .limit(20);

    if (tx.buchung_datum) {
      query = query
        .gte('belegdatum', verschiebeTage(tx.buchung_datum, -10))
        .lte('belegdatum', verschiebeTage(tx.buchung_datum, 10));
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data as OffenerBeleg[]) || [];
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Passende Belege laden fehlgeschlagen:', err);
    return [];
  }
}

/**
 * Verknüpft einen Beleg mit der Bank-Transaktion (Geldausgang):
 *   eingangsbelege.import_transaktion_id = tx.id  UND  import_transaktionen.status = 'geklaert'.
 */
async function verknuepfeBelegMitZahlung(
  belegId: string,
  tx: ImportTransaktionRow,
): Promise<boolean> {
  try {
    const { error: bErr } = await supabase
      .from('eingangsbelege')
      .update({ import_transaktion_id: tx.id } as any)
      .eq('id', belegId);
    if (bErr) throw bErr;

    const { error: tErr } = await supabase
      .from('import_transaktionen')
      .update({ status: 'geklaert' } as any)
      .eq('id', tx.id);
    if (tErr) throw tErr;

    return true;
  } catch (err) {
    console.error('Beleg-Verknüpfung fehlgeschlagen:', err);
    return false;
  }
}

// ============================================================
// Kontokopf + Status-Leiste
// ============================================================
function KontoKopf({
  konto, status, cfg,
}: {
  konto: BankKontoKopf | null;
  status: BuchhaltungStatus | null;
  cfg: ReturnType<typeof useBankAbgleich>['cfg'];
}) {
  // IBAN-Fallback: erkennung[1] enthält die IBAN der Firma.
  const iban = (konto?.iban && konto.iban.trim()) || cfg.erkennung?.[1] || '—';
  const saldo = konto?.aktueller_saldo;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      {/* Bankkonto-Karte */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Landmark className="w-5 h-5 text-slate-500" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">
            {konto?.name || 'Geschäftskonto'}
          </div>
          <div className="text-xs text-slate-500 truncate">{konto?.bank_name || '—'}</div>
          <div className="text-xs text-slate-400 font-mono truncate">{iban}</div>
          {typeof saldo === 'number' && (
            <div className="text-sm font-semibold text-slate-900 mt-1">
              {fmtBetrag(saldo)} €
              <span className="text-xs font-normal text-slate-400 ml-1">Saldo</span>
            </div>
          )}
        </div>
      </div>

      {/* Status-Leiste */}
      <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatusKachel
          label="Offen"
          wert={status?.tx_offen ?? 0}
          akzent="neutral"
        />
        <StatusKachel
          label="Gebucht"
          wert={status?.buchungen_gebucht ?? 0}
          akzent="positiv"
        />
        <StatusKachel
          label="Belege ohne Zahlung"
          wert={status?.belege_ohne_zahlung ?? 0}
          akzent="neutral"
        />
        <StatusKachel
          label="Belege nicht gebucht"
          wert={status?.belege_nicht_gebucht ?? 0}
          akzent={(status?.belege_nicht_gebucht ?? 0) > 0 ? 'warnung' : 'neutral'}
        />
      </div>
    </div>
  );
}

function StatusKachel({
  label, wert, akzent = 'neutral',
}: {
  label: string;
  wert: number;
  akzent?: 'neutral' | 'positiv' | 'warnung';
}) {
  const zahlFarbe =
    akzent === 'positiv'
      ? 'text-emerald-600'
      : akzent === 'warnung'
      ? 'text-rose-600'
      : 'text-slate-900';
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm flex flex-col justify-between">
      <div className={`text-2xl font-semibold leading-none tabular-nums ${zahlFarbe}`}>{wert}</div>
      <div className="text-[11px] font-medium text-slate-500 mt-1.5 leading-tight">{label}</div>
    </div>
  );
}

// ============================================================
// Upload-Dialog
// ============================================================
function UploadDialog({
  firmaKey, konten, defaultKontoId, onClose, onImport, busy,
}: {
  firmaKey: FirmaKey;
  konten: FinanzKonto[];
  defaultKontoId: string;
  onClose: () => void;
  onImport: (zeilen: BankCsvZeile[], kontoId: string) => Promise<void>;
  busy: boolean;
}) {
  const [kontoId, setKontoId] = useState<string>(defaultKontoId);
  const [dateiName, setDateiName] = useState<string>('');
  const [zeilen, setZeilen] = useState<BankCsvZeile[]>([]);
  const [vorschau, setVorschau] = useState<BankCsvVorschau | null>(null);
  const [warnungen, setWarnungen] = useState<string[]>([]);
  const [uebersprungen, setUebersprungen] = useState(0);
  const [erkannteFirma, setErkannteFirma] = useState<FirmaKey | null>(null);
  const [analysiert, setAnalysiert] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleDatei = useCallback(async (file: File) => {
    setDateiName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const text = new TextDecoder('windows-1252').decode(buf);
      const res = parseSparkassenCsv(text);
      setZeilen(res.zeilen);
      setVorschau(berechneVorschau(res.zeilen));
      setWarnungen(res.warnungen);
      setUebersprungen(res.uebersprungen);
      setErkannteFirma(erkenneFirmaAusZeilen(res.zeilen));
      setAnalysiert(true);
      if (res.zeilen.length === 0) {
        toast.error('Keine gültigen Buchungen in der Datei gefunden.');
      }
    } catch (e) {
      console.error(e);
      toast.error('Datei konnte nicht gelesen werden.');
    }
  }, []);

  const erkanntPasst = analysiert && erkannteFirma === firmaKey;
  const erkanntFalsch = analysiert && erkannteFirma !== null && erkannteFirma !== firmaKey;
  const erkanntKein = analysiert && erkannteFirma === null;
  const aktuellLabel = FIRMA_ABGLEICH[firmaKey]?.label || firmaKey;
  const erkanntLabel = erkannteFirma ? (FIRMA_ABGLEICH[erkannteFirma]?.label || erkannteFirma) : '';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-slate-500" />
            <h2 className="text-base font-semibold text-slate-900">Bank-CSV importieren</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Zielkonto
            </label>
            <select
              value={kontoId}
              onChange={(e) => setKontoId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
            >
              {konten.length === 0 && <option value={defaultKontoId}>Geschäftskonto (Standard)</option>}
              {konten.map((k) => (
                <option key={k.id} value={k.id}>{k.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              CSV-Datei (Sparkassen-Format)
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleDatei(f); }}
              className="hidden"
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-xl text-sm text-slate-600 hover:border-slate-400 hover:bg-slate-50 transition-colors"
            >
              <Upload className="w-4 h-4" />
              {dateiName || 'Datei auswählen…'}
            </button>
          </div>

          {erkanntPasst && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800 flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Erkanntes Konto: {aktuellLabel}</span>
            </div>
          )}
          {erkanntFalsch && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Achtung: Diese Datei gehört zu {erkanntLabel}, nicht {aktuellLabel}. Bitte oben
                auf {erkanntLabel} wechseln und dort importieren.
              </span>
            </div>
          )}
          {erkanntKein && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Konto nicht erkannt — Zielkonto prüfen.</span>
            </div>
          )}

          {warnungen.length > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-700 space-y-1">
              {warnungen.map((w, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {w}
                </div>
              ))}
            </div>
          )}

          {vorschau && vorschau.anzahl > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Vorschau</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-gray-400 text-xs">Buchungen</div>
                  <div className="font-bold text-gray-900">{vorschau.anzahl}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">Zeitraum</div>
                  <div className="font-bold text-gray-900">{fmtDatum(vorschau.von)} – {fmtDatum(vorschau.bis)}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">Summe Eingänge</div>
                  <div className="font-semibold text-emerald-600">+{fmtBetrag(vorschau.summeEingang)} €</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">Summe Ausgänge</div>
                  <div className="font-semibold text-slate-700">{fmtBetrag(vorschau.summeAusgang)} €</div>
                </div>
              </div>
              {uebersprungen > 0 && (
                <div className="mt-3 text-xs text-amber-600">
                  {uebersprungen} Zeile(n) ohne gültiges Datum/Betrag werden übersprungen.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-5 border-t flex gap-3">
          <button
            onClick={() => onImport(zeilen, kontoId)}
            disabled={busy || zeilen.length === 0 || erkanntFalsch}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-colors disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            Importieren {zeilen.length > 0 ? `(${zeilen.length})` : ''}
          </button>
          <button onClick={onClose} className="px-4 py-3 bg-gray-100 text-gray-600 rounded-xl font-medium hover:bg-gray-200">
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Forderungs-Restbetrag (read-only) — Geldeingaenge mit Aktenzeichen
// ============================================================
function ForderungsRestbetrag({ info }: { info: ForderungsInfo }) {
  const oeffneForderungsmanager = () => {
    try {
      window.location.assign(`/forderungen?akte_id=${encodeURIComponent(info.akteId)}`);
    } catch {
      // robust — Navigation darf nie crashen.
    }
  };

  return (
    <div className="mb-3">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <Receipt className="w-3.5 h-3.5" /> Rechnungen zur Akte {info.aktenNr}
      </div>
      <div className="space-y-1.5">
        {info.rechnungen.map((r) => {
          const brutto = Number(r.brutto_betrag) || 0;
          const bezahlt = Number(r.bezahlt_betrag) || 0;
          const kuerzung = Number(r.kuerzung_betrag) || 0;
          const offen = r.offen;
          const hatOffen = offen > 0.0001;
          return (
            <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-slate-900 truncate">
                  {r.rechnungs_nr || '(ohne Nummer)'}
                </div>
                {r.status && (
                  <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                    {r.status}
                  </span>
                )}
              </div>
              <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-xs">
                <div>
                  <div className="text-slate-400">Brutto</div>
                  <div className="font-medium text-slate-700 tabular-nums">{fmtBetrag(brutto)} €</div>
                </div>
                <div>
                  <div className="text-slate-400">Bezahlt</div>
                  <div className="font-medium text-slate-700 tabular-nums">{fmtBetrag(bezahlt)} €</div>
                </div>
                <div>
                  <div className="text-slate-400">Kürzung</div>
                  <div className="font-medium text-slate-700 tabular-nums">{fmtBetrag(kuerzung)} €</div>
                </div>
                <div>
                  <div className="text-slate-400">Offen</div>
                  <div className={`font-semibold tabular-nums ${hatOffen ? 'text-rose-600' : 'text-emerald-600'}`}>
                    {fmtBetrag(offen)} €
                  </div>
                </div>
              </div>
              {hatOffen && (
                <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[11px] text-slate-500">
                    Offene Restforderung: <span className="font-semibold text-rose-600">{fmtBetrag(offen)} €</span>
                  </div>
                  <button
                    type="button"
                    onClick={oeffneForderungsmanager}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-100"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Im Forderungsmanager öffnen
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// Beleg-Miniatur (nur bei sicher verknüpftem Eingangsbeleg)
// ============================================================
function BelegMiniatur({ vorschau }: { vorschau: BelegVorschau }) {
  const [overlayOffen, setOverlayOffen] = useState(false);

  return (
    <div className="mb-3">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <Receipt className="w-3.5 h-3.5" /> Verknüpfter Beleg
      </div>

      {vorschau.istBild ? (
        <button
          type="button"
          onClick={() => setOverlayOffen(true)}
          className="block rounded-lg border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors"
          title="Beleg vergrößern"
        >
          <img
            src={vorschau.signedUrl}
            alt={vorschau.dateiName}
            className="max-h-32 w-auto object-contain bg-slate-50"
          />
        </button>
      ) : vorschau.istPdf ? (
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-slate-500" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate">{vorschau.dateiName}</div>
            <div className="text-[11px] text-slate-500">PDF-Dokument</div>
          </div>
          <a
            href={vorschau.signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-medium hover:bg-slate-900"
          >
            <ExternalLink className="w-3.5 h-3.5" /> PDF öffnen
          </a>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <ImageIcon className="w-4 h-4 text-slate-500" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate">{vorschau.dateiName}</div>
            <div className="text-[11px] text-slate-500">Datei</div>
          </div>
          <a
            href={vorschau.signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-medium hover:bg-slate-900"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Öffnen
          </a>
        </div>
      )}

      {overlayOffen && vorschau.istBild && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setOverlayOffen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                <Receipt className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <span className="truncate">{vorschau.dateiName}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={vorschau.signedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> In neuem Tab
                </a>
                <button
                  onClick={() => setOverlayOffen(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto bg-slate-50 flex items-center justify-center">
              <img
                src={vorschau.signedUrl}
                alt={vorschau.dateiName}
                className="max-w-full max-h-[75vh] w-auto object-contain rounded"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Zeilen-Aktionen (Abgleich / belegbasierte Buchung) — pro Transaktion
// ============================================================
function TransaktionsZeile({
  tx, abgleich, belegFirma, importFirma, buchungskonten, gegenkonto, onChanged,
}: {
  tx: ImportTransaktionRow;
  abgleich: ReturnType<typeof useBankAbgleich>;
  belegFirma: string;
  importFirma: string;
  buchungskonten: Buchungskonto[];
  gegenkonto: string;
  onChanged: () => void;
}) {
  const istEinnahme = tx.betrag > 0;
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState<RechnungsMatch[] | null>(null);
  const [ladeMatches, setLadeMatches] = useState(false);
  const [aktion, setAktion] = useState(false);

  const [belege, setBelege] = useState<OffenerBeleg[] | null>(null);
  const [ladeBelege, setLadeBelege] = useState(false);

  // Buchen: Buchungskonto-Auswahl + Buchungstext + Belegstatus.
  const erloesKonten = buchungskonten.filter((k) => k.typ === 'erloes');
  const aufwandKonten = buchungskonten.filter((k) => k.typ === 'aufwand');
  const kontoListe = istEinnahme
    ? (erloesKonten.length > 0 ? erloesKonten : buchungskonten)
    : (aufwandKonten.length > 0 ? aufwandKonten : buchungskonten);

  const [kontoId, setKontoId] = useState<string>('');
  const [buchungstext, setBuchungstext] = useState<string>(
    cleanVerwendungszweck(tx.rohtext || tx.beschreibung || ''),
  );
  const [belegInfo, setBelegInfo] = useState<{ vorhanden: boolean; belegId: string | null }>(
    { vorhanden: false, belegId: null },
  );
  const [vorhandeneBuchung, setVorhandeneBuchung] = useState<VorhandeneBuchung | null>(null);
  const [pruefeBuchung, setPruefeBuchung] = useState(true);

  // Schritt 2: Vorschau eines sicher verknüpften Belegs (nur bei vorhandener Verknüpfung).
  const [belegVorschau, setBelegVorschau] = useState<BelegVorschau | null>(null);
  const [belegVorschauGeladen, setBelegVorschauGeladen] = useState(false);

  // Schritt 3: Forderungs-Restbetrag (read-only) für Geldeingänge mit Aktenzeichen.
  const [forderungsInfo, setForderungsInfo] = useState<ForderungsInfo | null>(null);
  const [forderungsInfoGeladen, setForderungsInfoGeladen] = useState(false);

  // Beim Mount: existierende Buchung prüfen (für „Gebucht“-Badge).
  useEffect(() => {
    let aktiv = true;
    (async () => {
      const b = await ladeVorhandeneBuchung(tx.id, buchungskonten);
      if (aktiv) { setVorhandeneBuchung(b); setPruefeBuchung(false); }
    })();
    return () => { aktiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx.id, buchungskonten.length]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && istEinnahme && matches === null) {
      setLadeMatches(true);
      const m = await abgleich.findeRechnungsMatches(tx);
      setMatches(m);
      setLadeMatches(false);
    }
    if (next && !istEinnahme && tx.status !== 'geklaert' && belege === null) {
      setLadeBelege(true);
      const b = await ladePassendeBelege(belegFirma, tx);
      setBelege(b);
      setLadeBelege(false);
    }
    // Buchungs-Vorbereitung beim ersten Öffnen: Vorschlag + Belegstatus.
    if (next && !vorhandeneBuchung) {
      if (!kontoId) {
        const vorschlag = await bestimmeVorschlagKontoId(importFirma, tx, kontoListe);
        if (vorschlag) setKontoId(vorschlag);
      }
      const bi = await hatVerknuepftenBeleg(tx.id);
      setBelegInfo(bi);
    }
    // Schritt 2: Belegvorschau (nur bei sicher verknüpftem Beleg) einmalig laden.
    if (next && !belegVorschauGeladen) {
      try {
        const v = await ladeVerknuepfteBelegVorschau(tx.id);
        setBelegVorschau(v);
      } catch {
        setBelegVorschau(null);
      }
      setBelegVorschauGeladen(true);
    }
    // Schritt 3: Forderungs-Restbetrag (nur Geldeingänge mit Aktenzeichen, read-only).
    if (next && istEinnahme && !forderungsInfoGeladen) {
      try {
        const info = await ladeForderungsInfo(tx.rohtext || tx.beschreibung || '');
        setForderungsInfo(info);
      } catch {
        setForderungsInfo(null);
      }
      setForderungsInfoGeladen(true);
    }
  };

  const handleBuchen = async (zielStatus: 'gebucht' | 'vorerfasst') => {
    if (!kontoId) {
      toast.error('Bitte ein Buchungskonto wählen.');
      return;
    }
    setAktion(true);
    const konto = buchungskonten.find((k) => k.id === kontoId);
    const res = await fuehreBuchungAus({
      importFirma,
      tx,
      buchungskontoId: kontoId,
      buchungstext,
      gegenkonto,
      belegVorhanden: belegInfo.vorhanden,
      belegId: belegInfo.belegId,
      status: zielStatus,
    });
    setAktion(false);
    if (res.ok) {
      const label = konto ? `${konto.nummer ?? ''} ${konto.bezeichnung ?? ''}`.trim() : kontoId;
      if (zielStatus === 'gebucht') {
        toast.success(`Gebucht auf ${label}`);
      } else {
        toast.success(`Als vorerfasst gebucht auf ${label}`);
      }
      const b = await ladeVorhandeneBuchung(tx.id, buchungskonten);
      setVorhandeneBuchung(b);
      onChanged();
    } else {
      toast.error(`Buchung fehlgeschlagen: ${res.fehler || 'unbekannt'}`);
    }
  };

  // GoBD: Ausgabe ohne Beleg darf nicht final „gebucht“ werden.
  const belegFehltBeiAusgabe = !istEinnahme && !belegInfo.vorhanden;

  const handleZahlung = async (m: RechnungsMatch) => {
    setAktion(true);
    const ok = await abgleich.alsZahlungBuchen(tx, m.rechnung);
    setAktion(false);
    if (ok) {
      toast.success(`Zahlung gebucht — Rechnung ${m.rechnung.rechnungs_nr} auf „bezahlt“ gesetzt.`, {
        description: 'Rückgängig: Status der Rechnung und Transaktion manuell zurücksetzen.',
      });
      onChanged();
    } else {
      toast.error('Buchung der Zahlung fehlgeschlagen.');
    }
  };

  const handleVerknuepfen = async (beleg: OffenerBeleg) => {
    setAktion(true);
    const ok = await verknuepfeBelegMitZahlung(beleg.id, tx);
    setAktion(false);
    if (ok) {
      toast.success('Beleg mit Zahlung verknüpft — Buchung geklärt.', {
        description: 'Rückgängig: import_transaktion_id und Status manuell zurücksetzen.',
      });
      onChanged();
    } else {
      toast.error('Verknüpfung fehlgeschlagen.');
    }
  };

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        onClick={toggle}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
      >
        <span className="text-slate-300 flex-shrink-0">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-slate-100">
          {istEinnahme ? <ArrowUpRight className="w-4 h-4 text-emerald-600" /> : <ArrowDownRight className="w-4 h-4 text-slate-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-sm font-medium text-slate-900 truncate"
            title={tx.beschreibung || ''}
          >
            {cleanVerwendungszweck(tx.beschreibung || '') || '(ohne Beschreibung)'}
          </div>
          <div className="text-xs text-slate-500">
            {fmtDatum(tx.buchung_datum)}
            <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${tx.status === 'geklaert' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
              {STATUS_LABEL[tx.status] || tx.status}
            </span>
            {vorhandeneBuchung && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-100 inline-flex items-center gap-1">
                <BookCheck className="w-3 h-3" />
                Gebucht{vorhandeneBuchung.nummer ? `: ${vorhandeneBuchung.nummer}` : ''}
              </span>
            )}
          </div>
        </div>
        <div className={`text-sm font-semibold tabular-nums flex-shrink-0 ${istEinnahme ? 'text-emerald-600' : 'text-slate-700'}`}>
          {istEinnahme ? '+' : ''}{fmtBetrag(tx.betrag)} {tx.waehrung}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 bg-slate-50/60">
          {/* Schritt 2: Belegvorschau — nur wenn sicher verknüpfter Beleg vorhanden */}
          {belegVorschau && <BelegMiniatur vorschau={belegVorschau} />}

          {/* Schritt 3: Forderungs-Restbetrag (read-only) — nur Geldeingänge mit Aktenzeichen */}
          {forderungsInfo && <ForderungsRestbetrag info={forderungsInfo} />}

          {/* Buchen-Bereich (Buchungskonto-Vorschlag + Buchen) */}
          {vorhandeneBuchung ? (
            <div className="mb-3 bg-emerald-50/70 border border-emerald-100 rounded-lg p-3 text-xs text-emerald-800 flex items-center gap-2">
              <BookCheck className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                Bereits gebucht
                {vorhandeneBuchung.nummer ? ` auf ${vorhandeneBuchung.nummer}` : ''}
                {vorhandeneBuchung.bezeichnung ? ` · ${vorhandeneBuchung.bezeichnung}` : ''}
                {vorhandeneBuchung.status ? ` (${vorhandeneBuchung.status})` : ''}.
              </span>
            </div>
          ) : (
            <div className="mb-3 bg-white border border-slate-200 rounded-lg p-3 shadow-sm space-y-2.5">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <BookCheck className="w-3.5 h-3.5" /> Buchen
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Buchungskonto</label>
                <select
                  value={kontoId}
                  onChange={(e) => setKontoId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                >
                  <option value="">— Konto wählen —</option>
                  {kontoListe.map((k) => (
                    <option key={k.id} value={k.id}>
                      {(k.nummer ?? '') + ' · ' + (k.bezeichnung ?? '')}
                    </option>
                  ))}
                </select>
                {buchungskonten.length === 0 && (
                  <div className="text-[11px] text-amber-600 mt-1">
                    Keine Buchungskonten gefunden — bitte Kontenrahmen prüfen.
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Buchungstext</label>
                <input
                  type="text"
                  value={buchungstext}
                  onChange={(e) => setBuchungstext(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                  placeholder="Buchungstext"
                />
              </div>

              {belegFehltBeiAusgabe && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-[11px] text-amber-800 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>Beleg fehlt — bitte zuordnen/hochladen. Endgültiges Buchen (GoBD) erst mit Beleg möglich.</span>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-0.5">
                <button
                  onClick={() => handleBuchen('gebucht')}
                  disabled={aktion || !kontoId || belegFehltBeiAusgabe}
                  title={belegFehltBeiAusgabe ? 'Bei Ausgaben ohne Beleg gesperrt (GoBD)' : undefined}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-40"
                >
                  {aktion ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookCheck className="w-3.5 h-3.5" />}
                  Buchen
                </button>
                {belegFehltBeiAusgabe && (
                  <button
                    onClick={() => handleBuchen('vorerfasst')}
                    disabled={aktion || !kontoId}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 disabled:opacity-40"
                  >
                    {aktion ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Als vorerfasst buchen
                  </button>
                )}
              </div>
            </div>
          )}

          {tx.status !== 'geklaert' && istEinnahme && (
            <div className="mb-1">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Link2 className="w-3.5 h-3.5" /> Passende offene Rechnungen
              </div>
              {ladeMatches ? (
                <div className="text-xs text-gray-400 flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Suche…
                </div>
              ) : matches && matches.length > 0 ? (
                <div className="space-y-1.5">
                  {matches.map((m) => (
                    <div key={m.rechnung.id} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg p-2.5">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {m.rechnung.rechnungs_nr} · {m.rechnung.empfaenger_name || '—'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {fmtBetrag(Number(m.rechnung.brutto_betrag) || 0)} € ·{' '}
                          <span className={m.grund === 'betrag' ? 'text-emerald-600' : m.grund === 'ak' ? 'text-teal-600' : 'text-blue-600'}>
                            {m.grund === 'betrag' ? 'Betrag passt' : m.grund === 'ak' ? 'AK-Nummer im Text' : 'Referenz im Text'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleZahlung(m)}
                        disabled={aktion}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-40"
                      >
                        {aktion ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        Als Zahlung buchen
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>Keine Rechnung/Akte gefunden — bitte zuordnen. Keine Buchung ohne Zuordnung.</span>
                </div>
              )}
            </div>
          )}

          {tx.status !== 'geklaert' && !istEinnahme && (
            <div className="mb-1">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Receipt className="w-3.5 h-3.5" /> Passende Belege
              </div>
              {ladeBelege ? (
                <div className="text-xs text-gray-400 flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Suche…
                </div>
              ) : belege && belege.length > 0 ? (
                <div className="space-y-1.5">
                  {belege.map((b) => (
                    <div key={b.id} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg p-2.5">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {b.absender_firma || b.absender_name || b.verwendungszweck || '(ohne Absender)'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {fmtBetrag(Number(b.brutto_betrag) || 0)} € · {fmtDatum(b.belegdatum)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleVerknuepfen(b)}
                        disabled={aktion}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-medium hover:bg-slate-900 disabled:opacity-40"
                      >
                        {aktion ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                        Beleg verknüpfen
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
                  <Receipt className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>Kein passender Beleg — bitte Beleg hochladen (Eingangsrechnungen). Keine Buchung ohne Beleg (GoBD).</span>
                </div>
              )}
            </div>
          )}

          {tx.status === 'geklaert' && !vorhandeneBuchung && (
            <div className="text-xs text-emerald-600 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Bereits geklärt / gebucht.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Hauptkomponente
// ============================================================
export default function KontoauszuegePage({ firmaKey = 'sv' }: { firmaKey?: FirmaKey }) {
  const abgleich = useBankAbgleich(firmaKey);
  const { cfg, ladeTransaktionen } = abgleich;
  const belegFirma = BELEG_FIRMA[firmaKey] || BELEG_FIRMA.sv;
  const finanzKontoFirma = FINANZ_KONTO_FIRMA[firmaKey] || FINANZ_KONTO_FIRMA.sv;
  const gegenkonto = GEGENKONTO[firmaKey] || GEGENKONTO.sv;

  const [konten, setKonten] = useState<FinanzKonto[]>([]);
  const [transaktionen, setTransaktionen] = useState<ImportTransaktionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [letzterUpload, setLetzterUpload] = useState<string | null>(null);

  // Cockpit: Kontokopf + Status-Leiste.
  const [bankKonto, setBankKonto] = useState<BankKontoKopf | null>(null);
  const [status, setStatus] = useState<BuchhaltungStatus | null>(null);
  // Buchungskonten der Firma (für Vorschlag + Dropdown im Detailbereich).
  const [buchungskonten, setBuchungskonten] = useState<Buchungskonto[]>([]);

  const heuteMonat = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [monat, setMonat] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const ladeListe = useCallback(async () => {
    setLoading(true);
    const tx = await ladeTransaktionen({
      monat: monat || undefined,
      status: statusFilter || undefined,
    });
    setTransaktionen(tx);
    setLoading(false);
  }, [ladeTransaktionen, monat, statusFilter]);

  // Status-Leiste neu laden (auch nach Buchungen).
  const ladeCockpit = useCallback(async () => {
    const s = await ladeBuchhaltungStatus(cfg.importFirma);
    setStatus(s);
  }, [cfg.importFirma]);

  // Stammdaten + Liste laden (bei Firma-/Filter-Wechsel)
  useEffect(() => {
    let aktiv = true;
    (async () => {
      const [k, letzte, konto, s, bk] = await Promise.all([
        abgleich.ladeKonten(),
        abgleich.letztesUploadDatum(),
        ladeBankKonto(finanzKontoFirma),
        ladeBuchhaltungStatus(cfg.importFirma),
        ladeBuchungskonten(cfg.importFirma),
      ]);
      if (!aktiv) return;
      setKonten(k);
      setLetzterUpload(letzte);
      setBankKonto(konto);
      setStatus(s);
      setBuchungskonten(bk);
    })();
    return () => { aktiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaKey]);

  useEffect(() => { ladeListe(); }, [ladeListe]);

  const tageSeitUpload = useMemo(() => {
    if (!letzterUpload) return null;
    const d = new Date(letzterUpload);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  }, [letzterUpload]);

  const handleImport = useCallback(async (zeilen: BankCsvZeile[], kontoId: string) => {
    if (zeilen.length === 0) { toast.error('Keine Buchungen zum Importieren.'); return; }
    const res = await abgleich.importiere(zeilen, kontoId);
    if (res.fehler > 0) {
      toast.error(`Import mit Fehlern: ${res.neu} neu, ${res.duplikate} Duplikate, ${res.fehler} Fehler.`);
    } else {
      toast.success(`${res.neu} neu importiert, ${res.duplikate} Duplikate übersprungen.`);
    }
    setShowUpload(false);
    const letzte = await abgleich.letztesUploadDatum();
    setLetzterUpload(letzte);
    await ladeListe();
    await ladeCockpit();
  }, [abgleich, ladeListe, ladeCockpit]);

  // Nach Zeilen-Aktion: Liste + Status neu laden.
  const handleZeileGeaendert = useCallback(async () => {
    await ladeListe();
    await ladeCockpit();
  }, [ladeListe, ladeCockpit]);

  return (
    <div className="space-y-4">
      {/* Kopf */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Bank — {cfg.label}</h2>
          <p className="text-xs text-slate-500">Wöchentlicher Bank-CSV-Upload und Abgleich</p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 shadow-sm"
        >
          <Upload className="w-4 h-4" /> CSV importieren
        </button>
      </div>

      {/* Kontokopf + Status-Leiste */}
      <KontoKopf konto={bankKonto} status={status} cfg={cfg} />

      {/* Erinnerungs-Banner */}
      {tageSeitUpload !== null && tageSeitUpload > 7 && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm flex items-start gap-2.5">
          <Clock className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-slate-600">
            Letzter Bank-Upload vor <strong>{tageSeitUpload} Tagen</strong> — ein wöchentlicher Upload wird empfohlen.
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={monat}
          onChange={(e) => setMonat(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white"
        >
          <option value="">Alle Monate</option>
          <option value={heuteMonat}>Aktueller Monat</option>
          {Array.from({ length: 12 }).map((_, i) => {
            const d = new Date();
            d.setMonth(d.getMonth() - i);
            const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            return <option key={v} value={v}>{d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</option>;
          })}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white"
        >
          <option value="">Alle Status</option>
          <option value="unklar">Offen</option>
          <option value="geklaert">Geklärt</option>
          <option value="sicher">Automatisch</option>
          <option value="ignoriert">Ignoriert</option>
        </select>
        <button
          onClick={ladeListe}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
        >
          <RefreshCw className="w-4 h-4" /> Aktualisieren
        </button>
        <div className="text-sm text-slate-400 ml-auto">{transaktionen.length} Buchungen</div>
      </div>

      {/* Liste */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : transaktionen.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <FileSpreadsheet className="w-10 h-10 mb-2" />
            <p className="text-sm">Keine Buchungen in dieser Ansicht.</p>
            <p className="text-xs mt-1">Laden Sie einen Bank-CSV-Export hoch, um zu starten.</p>
          </div>
        ) : (
          transaktionen.map((tx) => (
            <TransaktionsZeile
              key={tx.id}
              tx={tx}
              abgleich={abgleich}
              belegFirma={belegFirma}
              importFirma={cfg.importFirma}
              buchungskonten={buchungskonten}
              gegenkonto={gegenkonto}
              onChanged={handleZeileGeaendert}
            />
          ))
        )}
      </div>

      {showUpload && (
        <UploadDialog
          firmaKey={firmaKey}
          konten={konten}
          defaultKontoId={cfg.defaultKontoId}
          onClose={() => setShowUpload(false)}
          onImport={handleImport}
          busy={abgleich.busy}
        />
      )}
    </div>
  );
}
